import React from "react";
import LinearProgress from "@mui/material/LinearProgress";
import Avatar from "@mui/material/Avatar";
import { fileIcons } from "../../../utils/icon-mimetypes";
import FileBg from "../../../generators/fileBGGenerator";

export default function ConfigureDeliverableModal({
  theme,
  sourceMode,
  // edit mode: reconfigure an existing deliverable, file is already uploaded
  isEditMode = false,
  editName,
  setEditName,
  onSubmit,
  onCancel,
  // local upload
  selectedLocalFile,
  setSelectedLocalFile,
  customFileName,
  setCustomFileName,
  uploadProgress,
  formatFileSize,
  // org vault
  vaultSearch,
  setVaultSearch,
  normalizedVaultFiles,
  selectedVaultFile,
  setSelectedVaultFile,
  hexToRgba,
  // shared config
  paywallPrice,
  setPaywallPrice,
  previewLimit,
  setPreviewLimit,
  submittingDeliverable,
}) {
  const filteredVaultFiles = normalizedVaultFiles.filter((f) =>
    f.name?.toLowerCase().includes(vaultSearch.toLowerCase())
  );

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1200,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          background: theme.cardBg,
          border: `1px solid ${theme.border}`,
          borderRadius: "12px",
          padding: "24px",
          width: sourceMode === "vault" ? "85vw" : "460px",
          maxWidth: sourceMode === "vault" ? "1100px" : undefined,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div>
          <h3
            style={{
              margin: "0 0 4px 0",
              color: theme.textMain,
              fontSize: "16px",
            }}
          >
            {isEditMode ? "Edit Deliverable" : "Configure Deliverable"}
          </h3>
          <p style={{ fontSize: "12px", color: theme.textSub, margin: 0 }}>
            {isEditMode
              ? "Update this deliverable's settings. The uploaded file stays the same."
              : "Set up access parameter security metrics prior to whitelisting pipeline exposure."}
          </p>
        </div>

        {isEditMode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "600",
                color: theme.textMain,
              }}
            >
              Display Name
            </label>
            <input
              type="text"
              required
              placeholder="Enter display name..."
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: `1px solid ${theme.border}`,
                borderRadius: "6px",
                background: theme.bg,
                color: theme.textMain,
                fontSize: "13px",
              }}
            />
          </div>
        ) : sourceMode === "local" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "600",
                color: theme.textMain,
              }}
            >
              Select Asset File
            </label>
            <input
              type="file"
              required
              onChange={(e) => setSelectedLocalFile(e.target.files[0] || null)}
              style={{ fontSize: "13px", color: theme.textMain }}
            />
            {selectedLocalFile && (
              <div
                style={{
                  fontSize: "12px",
                  color: theme.textSub,
                  background: theme.bg,
                  padding: "6px",
                  borderRadius: "4px",
                }}
              >
                Size: {formatFileSize(selectedLocalFile.size)}
              </div>
            )}
            {uploadProgress > 0 && (
              <div style={{ marginTop: "4px" }}>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <div
                  style={{
                    fontSize: "11px",
                    textAlign: "right",
                    color: theme.textSub,
                    marginTop: "2px",
                  }}
                >
                  {Math.round(uploadProgress)}%
                </div>
              </div>
            )}
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "600",
                color: theme.textMain,
                marginTop: "8px",
              }}
            >
              Customize Display Name (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter custom title name..."
              value={customFileName}
              onChange={(e) => setCustomFileName(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: `1px solid ${theme.border}`,
                borderRadius: "6px",
                background: theme.bg,
                color: theme.textMain,
                fontSize: "13px",
              }}
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              flex: 1,
              minHeight: 0,
            }}
          >
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "600",
                color: theme.textMain,
              }}
            >
              Select File from Org Vault
            </label>
            <input
              type="text"
              placeholder="Search org file name..."
              value={vaultSearch}
              onChange={(e) => setVaultSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: `1px solid ${theme.border}`,
                borderRadius: "6px",
                background: theme.bg,
                color: theme.textMain,
                fontSize: "13px",
              }}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "16px",
                flex: 1,
                overflowY: "auto",
                padding: "8px",
                maxHeight: "50vh",
              }}
            >
              {filteredVaultFiles.length === 0 ? (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    padding: "40px",
                    textAlign: "center",
                    color: theme.textSub,
                    fontStyle: "italic",
                    fontSize: "14px",
                  }}
                >
                  No files in the organizational vault match your search.
                </div>
              ) : (
                filteredVaultFiles.map((f) => {
                  const isPicked = selectedVaultFile?.id === f.id;
                  const resolvedColor = f.color || "#5E5ADB";
                  const backgroundAlpha = hexToRgba(resolvedColor, 0.1);
                  const fileMimeIcon =
                    fileIcons[f.mimetype] ||
                    `${process.env.PUBLIC_URL}/assets/defaulticon.webp`;
                  return (
                    <div
                      key={f.id}
                      onClick={() => setSelectedVaultFile(f)}
                      style={{
                        backgroundColor: backgroundAlpha,
                        backgroundImage: FileBg({
                          gradientColors: [
                            resolvedColor,
                            resolvedColor,
                            resolvedColor,
                            resolvedColor,
                          ],
                        }),
                        backgroundSize: "cover",
                        backgroundPosition: "center center",
                        backgroundRepeat: "no-repeat",
                        backgroundBlendMode: "lighten",
                        border: isPicked
                          ? "2px solid #5E5ADB"
                          : `1px solid ${theme.border}`,
                        borderRadius: "16px",
                        padding: "16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        cursor: "pointer",
                        position: "relative",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={fileMimeIcon}
                        alt="mime type preview"
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "contain",
                          marginTop: "12px",
                        }}
                      />
                      <div style={{ width: "100%" }}>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: theme.textMain,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {f.name?.split("_TS=")[0]}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "8px",
                            width: "100%",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "10px",
                              color: theme.textSub,
                              fontWeight: "700",
                            }}
                          >
                            {f.size || "—"}
                          </span>
                          <Avatar
                            src={f.profilePic}
                            sx={{
                              width: 22,
                              height: 22,
                              border: "1px solid silver",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "600",
                color: theme.textMain,
                marginBottom: "4px",
              }}
            >
              Paywall Price (Rs)
            </label>
            <input
              type="number"
              min="0"
              placeholder="0.00 (Free)"
              value={paywallPrice}
              onChange={(e) => setPaywallPrice(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: `1px solid ${theme.border}`,
                borderRadius: "6px",
                background: theme.bg,
                color: theme.textMain,
                fontSize: "13px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "600",
                color: theme.textMain,
                marginBottom: "4px",
              }}
            >
              Preview Page Limit
            </label>
            <input
              type="number"
              min="0"
              placeholder="0 (Full File)"
              value={previewLimit}
              onChange={(e) => setPreviewLimit(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: `1px solid ${theme.border}`,
                borderRadius: "6px",
                background: theme.bg,
                color: theme.textMain,
                fontSize: "13px",
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "8px",
            borderTop: `1px solid ${theme.border}`,
            paddingTop: "12px",
          }}
        >
          <button type="button" onClick={onCancel} style={{
            background: "transparent",
            border: "none",
            color: theme.textSub,
            padding: "8px 16px",
            cursor: "pointer",
            fontSize: "13px",
          }}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={submittingDeliverable}
            style={{
              background: "#5E5ADB",
              color: "#fff",
              border: "none",
              padding: "8px 20px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            {submittingDeliverable
              ? "Processing..."
              : isEditMode
              ? "Save Changes"
              : "Add to Deliverables"}
          </button>
        </div>
      </form>
    </div>
  );
}
